import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle,
    Loader2, ArrowRight, ArrowLeft, XCircle, Check
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useProjects } from '../../context/AppContext';
import {
    downloadTemplate,
    parseExcelFile,
    convertToEpicPayloads,
    ImportRow,
    ValidationError,
    ParsedImportResult
} from '../../utils/excelImport';
import { useApi } from '../../hooks/useApi';

interface ImportEpicsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete: () => void;
}

type Step = 'upload' | 'preview' | 'importing' | 'complete';

interface ImportResult {
    created: number;
    updated: number;
    failed: number;
}

export function ImportEpicsModal({ isOpen, onClose, onComplete }: ImportEpicsModalProps) {
    const { projects } = useProjects();
    const api = useApi();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<Step>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [parseResult, setParseResult] = useState<ParsedImportResult | null>(null);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [dragOver, setDragOver] = useState(false);

    const resetState = () => {
        setStep('upload');
        setFile(null);
        setParseResult(null);
        setImporting(false);
        setProgress(0);
        setResult(null);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleFileDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile?.name.endsWith('.xlsx')) {
            await processFile(droppedFile);
        }
    }, [projects]);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            await processFile(selectedFile);
        }
    };

    const processFile = async (selectedFile: File) => {
        setFile(selectedFile);
        const result = await parseExcelFile(selectedFile, projects);
        setParseResult(result);
        setStep('preview');
    };

    const handleImport = async () => {
        if (!parseResult) return;

        setStep('importing');
        setImporting(true);
        setProgress(0);

        const payloads = convertToEpicPayloads(parseResult.rows, projects);
        let created = 0;
        let updated = 0;
        let failed = 0;

        for (let i = 0; i < payloads.length; i++) {
            try {
                // Try to find existing epic by title + project
                const existingEpics = await api.getEpics();
                const matches = existingEpics.filter(
                    (e: any) => e.title === payloads[i].title && e.projectId === payloads[i].projectId
                );

                if (matches.length > 0) {
                    // Update existing
                    await api.updateEpic(matches[0].id, payloads[i]);
                    updated++;
                } else {
                    // Create new
                    await api.createEpic(payloads[i]);
                    created++;
                }
            } catch (err) {
                console.error('Import error:', err);
                failed++;
            }

            setProgress(Math.round(((i + 1) / payloads.length) * 100));
        }

        setResult({ created, updated, failed });
        setImporting(false);
        setStep('complete');
    };

    const getErrorsForRow = (rowIndex: number, errors: ValidationError[]): ValidationError[] => {
        return errors.filter(e => e.row === rowIndex + 2);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50"
                        onClick={handleClose}
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="w-full max-w-3xl max-h-[85vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col"
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-500">
                                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                    <FileSpreadsheet className="w-5 h-5" />
                                    Import Epics from Excel
                                </h2>
                                <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                                    <X className="w-5 h-5 text-white" />
                                </button>
                            </div>

                            {/* Progress Steps */}
                            <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between max-w-md mx-auto">
                                    {['Upload', 'Preview', 'Import'].map((label, idx) => {
                                        const stepIndex = ['upload', 'preview', 'importing', 'complete'].indexOf(step);
                                        const isActive = idx <= stepIndex;
                                        const isCurrent = idx === Math.min(stepIndex, 2);
                                        return (
                                            <React.Fragment key={label}>
                                                <div className="flex items-center gap-2">
                                                    <div className={cn(
                                                        "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all",
                                                        isActive ? "bg-amber-500 text-white" : "bg-slate-200 dark:bg-slate-700 text-slate-500"
                                                    )}>
                                                        {isActive && idx < stepIndex ? <Check className="w-4 h-4" /> : idx + 1}
                                                    </div>
                                                    <span className={cn(
                                                        "text-xs font-semibold",
                                                        isCurrent ? "text-amber-600 dark:text-amber-400" : "text-slate-500"
                                                    )}>{label}</span>
                                                </div>
                                                {idx < 2 && <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 mx-3" />}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {/* Step 1: Upload */}
                                {step === 'upload' && (
                                    <div className="space-y-6">
                                        {/* Template Download */}
                                        <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                            <div>
                                                <h4 className="font-semibold text-blue-800 dark:text-blue-300">Need a template?</h4>
                                                <p className="text-sm text-blue-600 dark:text-blue-400">Download pre-formatted Excel with sample data</p>
                                            </div>
                                            <button
                                                onClick={downloadTemplate}
                                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                            >
                                                <Download className="w-4 h-4" /> Template
                                            </button>
                                        </div>

                                        {/* Drag & Drop Zone */}
                                        <div
                                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                            onDragLeave={() => setDragOver(false)}
                                            onDrop={handleFileDrop}
                                            onClick={() => fileInputRef.current?.click()}
                                            className={cn(
                                                "border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all",
                                                dragOver
                                                    ? "border-amber-500 bg-amber-50 dark:bg-amber-900/10"
                                                    : "border-slate-300 dark:border-slate-700 hover:border-amber-400 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                            )}
                                        >
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".xlsx"
                                                onChange={handleFileSelect}
                                                className="hidden"
                                            />
                                            <Upload className={cn("w-12 h-12 mx-auto mb-4", dragOver ? "text-amber-500" : "text-slate-400")} />
                                            <h3 className="text-lg font-bold text-slate-700 dark:text-white mb-2">
                                                Drop your Excel file here
                                            </h3>
                                            <p className="text-sm text-slate-500">or click to browse (XLSX only)</p>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Preview */}
                                {step === 'preview' && parseResult && (
                                    <div className="space-y-4">
                                        {/* Summary */}
                                        <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800">
                                            <FileSpreadsheet className="w-8 h-8 text-amber-500" />
                                            <div className="flex-1">
                                                <div className="font-semibold text-slate-800 dark:text-white">{file?.name}</div>
                                                <div className="text-sm text-slate-500">{parseResult.rows.length} rows found</div>
                                            </div>
                                            {parseResult.errors.length > 0 ? (
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-semibold">
                                                    <AlertCircle className="w-4 h-4" /> {parseResult.errors.length} issues
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-semibold">
                                                    <CheckCircle2 className="w-4 h-4" /> Valid
                                                </div>
                                            )}
                                        </div>

                                        {/* Data Table */}
                                        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                            <div className="overflow-auto max-h-64">
                                                <table className="min-w-[900px] w-full text-sm">
                                                    <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Status</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-400 min-w-[200px]">Title</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Project</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Lane</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Prog</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Sec</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Biz</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Time</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Ops</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Conf</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">Eff</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">SGM</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-400 min-w-[150px]">Context</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {parseResult.rows.map((row, idx) => {
                                                            const rowErrors = getErrorsForRow(idx, parseResult.errors);
                                                            const hasError = rowErrors.length > 0;
                                                            return (
                                                                <tr key={idx} className={cn(
                                                                    "border-t border-slate-100 dark:border-slate-800",
                                                                    hasError && "bg-red-50 dark:bg-red-900/10"
                                                                )}>
                                                                    <td className="px-3 py-2">
                                                                        {hasError ? (
                                                                            <span className="flex items-center gap-1 text-red-500" title={rowErrors.map(e => `${e.column}: ${e.message}`).join(', ')}>
                                                                                <XCircle className="w-4 h-4" />
                                                                            </span>
                                                                        ) : (
                                                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2 font-medium text-slate-800 dark:text-white max-w-[200px] truncate" title={row.title}>{row.title || '—'}</td>
                                                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400 whitespace-nowrap">{row.projectName || '—'}</td>
                                                                    <td className="px-3 py-2">
                                                                        <span className={cn(
                                                                            "px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap",
                                                                            row.lane === 'Now' && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                                                                            row.lane === 'Next' && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                                                                            row.lane === 'Later' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                                                                            row.lane === 'Done' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                                                                            row.lane === 'Unassigned' && "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                                                                        )}>
                                                                            {row.lane}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.progress}%</td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.security}</td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.business}</td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.time}</td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.ops}</td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.confidence}</td>
                                                                    <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300 font-medium">{row.effort}</td>
                                                                    <td className="px-3 py-2 text-center">
                                                                        {row.sgm ? (
                                                                            <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                                                                        ) : (
                                                                            <span className="text-slate-400">—</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-400 text-xs max-w-[200px] truncate" title={row.notes}>{row.notes || '—'}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Error Details */}
                                        {parseResult.errors.length > 0 && (
                                            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                                <h4 className="font-semibold text-red-800 dark:text-red-300 mb-2">Validation Issues</h4>
                                                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 max-h-32 overflow-y-auto">
                                                    {parseResult.errors.map((err, idx) => (
                                                        <li key={idx}>Row {err.row}: {err.column} — {err.message}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Step 3: Importing */}
                                {step === 'importing' && (
                                    <div className="flex flex-col items-center justify-center py-12 space-y-6">
                                        <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
                                        <div className="text-center">
                                            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">Importing Epics...</h3>
                                            <p className="text-sm text-slate-500">Please wait while we process your data</p>
                                        </div>
                                        <div className="w-full max-w-xs">
                                            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                <motion.div
                                                    className="h-full bg-amber-500"
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <div className="text-center text-sm font-semibold text-slate-600 dark:text-slate-400 mt-2">{progress}%</div>
                                        </div>
                                    </div>
                                )}

                                {/* Step 4: Complete */}
                                {step === 'complete' && result && (
                                    <div className="flex flex-col items-center justify-center py-12 space-y-6">
                                        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                            <CheckCircle2 className="w-10 h-10 text-green-500" />
                                        </div>
                                        <div className="text-center">
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Import Complete!</h3>
                                            <p className="text-slate-500">Your epics have been processed</p>
                                        </div>
                                        <div className="flex gap-8">
                                            <div className="text-center">
                                                <div className="text-3xl font-black text-green-600">{result.created}</div>
                                                <div className="text-sm text-slate-500">Created</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-3xl font-black text-blue-600">{result.updated}</div>
                                                <div className="text-sm text-slate-500">Updated</div>
                                            </div>
                                            {result.failed > 0 && (
                                                <div className="text-center">
                                                    <div className="text-3xl font-black text-red-600">{result.failed}</div>
                                                    <div className="text-sm text-slate-500">Failed</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                                <div>
                                    {step === 'preview' && (
                                        <button
                                            onClick={resetState}
                                            className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                                        >
                                            <ArrowLeft className="w-4 h-4" /> Back
                                        </button>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleClose}
                                        className="px-4 py-2 text-slate-600 dark:text-slate-400 font-semibold hover:text-slate-800 dark:hover:text-white transition-colors"
                                    >
                                        {step === 'complete' ? 'Close' : 'Cancel'}
                                    </button>
                                    {step === 'preview' && (
                                        <button
                                            onClick={handleImport}
                                            disabled={!parseResult?.isValid}
                                            className={cn(
                                                "flex items-center gap-2 px-5 py-2 rounded-lg font-semibold transition-all",
                                                parseResult?.isValid
                                                    ? "bg-amber-500 text-white hover:bg-amber-600 shadow-lg shadow-amber-500/25"
                                                    : "bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed"
                                            )}
                                        >
                                            Import <ArrowRight className="w-4 h-4" />
                                        </button>
                                    )}
                                    {step === 'complete' && (
                                        <button
                                            onClick={() => { onComplete(); handleClose(); }}
                                            className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-lg shadow-green-500/25"
                                        >
                                            Done <Check className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default ImportEpicsModal;
